import { CompanyRequestError } from '@/company/api';

export type ShopifyField = 'shop' | 'clientId' | 'clientSecret';
export type ShopifyFieldErrors = Partial<Record<ShopifyField, string>>;
export const shopifyFieldLabels: Record<ShopifyField, string> = {
  shop: 'Store',
  clientId: 'Client ID',
  clientSecret: 'Client secret',
};

/** Local setup constraints only. Canonical shop parsing remains server-owned. */
export function shopifyFieldErrors(
  values: Record<ShopifyField, string>,
  message: (kind: 'fieldRequired' | 'fieldTooLong' | 'fieldInvalid', field: ShopifyField) => string,
): ShopifyFieldErrors {
  const errors: ShopifyFieldErrors = {};
  for (const field of ['shop', 'clientId', 'clientSecret'] as const) {
    const value = field === 'clientSecret' ? values[field] : values[field].trim();
    if (!value) errors[field] = message('fieldRequired', field);
    else if (value.length > { shop: 300, clientId: 256, clientSecret: 8192 }[field])
      errors[field] = message('fieldTooLong', field);
    else if (
      field === 'clientId' ? /\s/.test(value) : field === 'clientSecret' && /[\r\n\0]/.test(value)
    )
      errors[field] = message('fieldInvalid', field);
  }
  return errors;
}

/** A credential-pair refusal never identifies one credential as the bad field. */
export function shopifyErrorField(error: unknown): ShopifyField | null {
  if (!(error instanceof CompanyRequestError)) return null;
  if (error.code === 'shopify_shop_invalid') return 'shop';
  if (error.code !== 'invalid_request') return null;
  return error.path === 'shop' || error.path === 'clientId' || error.path === 'clientSecret'
    ? error.path
    : null;
}
